import { expandRect, isPointInRect, pointsBounds } from './photoMarkupGeometry'
import { IPoint, PhotoMarkup } from './photoMarkupModel'

function lerp(a: IPoint, b: IPoint, t: number): IPoint {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
}

function circleCrossings(a: IPoint, b: IPoint, c: IPoint, r: number) {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lengthSq = dx * dx + dy * dy
  if (!lengthSq) return []
  const fx = a.x - c.x
  const fy = a.y - c.y
  const halfB = fx * dx + fy * dy
  const discriminant = halfB * halfB - lengthSq * (fx * fx + fy * fy - r * r)
  if (discriminant <= 0) return []
  const root = Math.sqrt(discriminant)
  return [(-halfB - root) / lengthSq, (-halfB + root) / lengthSq]
    .filter(t => t > 0 && t < 1)
    .sort((x, y) => x - y)
}

export function clipOutsideCircle(
  path: IPoint[],
  center: IPoint,
  radius: number,
): IPoint[][] {
  const isInside = (p: IPoint): boolean =>
    (p.x - center.x) ** 2 + (p.y - center.y) ** 2 <= radius * radius
  const runs: IPoint[][] = []
  let run: IPoint[] = []
  const push = (p: IPoint): void => {
    const last = run[run.length - 1]
    if (!last || last.x !== p.x || last.y !== p.y) run.push(p)
  }
  const breakRun = (): void => {
    if (run.length >= 2) runs.push(run)
    run = []
  }
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const cuts = [0, ...circleCrossings(a, b, center, radius), 1]
    for (let k = 0; k < cuts.length - 1; k++) {
      if (isInside(lerp(a, b, (cuts[k] + cuts[k + 1]) / 2))) {
        breakRun()
      } else {
        push(lerp(a, b, cuts[k]))
        push(lerp(a, b, cuts[k + 1]))
      }
    }
  }
  breakRun()
  return runs
}

function brushSamples(from: IPoint, to: IPoint, radius: number): IPoint[] {
  const steps = Math.max(
    1,
    Math.ceil(Math.hypot(to.x - from.x, to.y - from.y) / (radius / 2)),
  )
  return Array.from({ length: steps }, (_, i) =>
    lerp(from, to, (i + 1) / steps),
  )
}

function isSamePath(a: IPoint[], b: IPoint[]): boolean {
  return (
    a.length === b.length && a.every((p, i) => p.x === b[i].x && p.y === b[i].y)
  )
}

function eraseAt(
  markups: PhotoMarkup[],
  center: IPoint,
  radius: number,
  createId: () => string,
): PhotoMarkup[] {
  let isChanged = false
  const next = markups.flatMap(markup => {
    if (markup.kind !== 'freehand' && markup.kind !== 'highlighter') {
      return [markup]
    }
    if (
      !isPointInRect(center, expandRect(pointsBounds(markup.points), radius))
    ) {
      return [markup]
    }
    const runs = clipOutsideCircle(markup.points, center, radius)
    if (runs.length === 1 && isSamePath(runs[0], markup.points)) {
      return [markup]
    }
    isChanged = true
    return runs.map(points => ({ ...markup, id: createId(), points }))
  })
  return isChanged ? next : markups
}

export function eraseAlong(
  markups: PhotoMarkup[],
  from: IPoint,
  to: IPoint,
  radius: number,
  createId: () => string,
): PhotoMarkup[] {
  const centers = from === to ? [to] : brushSamples(from, to, radius)
  return centers.reduce(
    (current, center) => eraseAt(current, center, radius, createId),
    markups,
  )
}
